/* @refresh reload */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLanguage } from './useLanguage'
import {
  DialogContext,
  type DialogHighlightKeyword,
  type DialogHighlightTone,
  type DialogOptions,
  type DialogVariant
} from './useDialog'

type DialogType = 'alert' | 'confirm'

type DialogState = DialogOptions & {
  type: DialogType
}

const DIALOG_HIGHLIGHT_TONE_CLASSES: Record<DialogHighlightTone, string> = {
  danger: 'bg-red-50 text-red-600 ring-red-100',
  warning: 'bg-amber-50 text-amber-700 ring-amber-100',
  info: 'bg-blue-50 text-blue-700 ring-blue-100',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  purple: 'bg-violet-50 text-violet-700 ring-violet-100',
}

const escapeRegExp = (value: string): string => value.replace(/[-/\^$*+?.()|[\]{}]/g, '\$&')

const buildDialogHighlightRegex = (highlights?: DialogHighlightKeyword[]): RegExp | null => {
  const keywords = (highlights || [])
    .map((item) => String(item.text || '').trim())
    .filter(Boolean)
    .sort((left, right) => right.length - left.length)
  if (keywords.length === 0) {
    return null
  }
  return new RegExp('(' + keywords.map(escapeRegExp).join('|') + ')', 'g')
}

const renderDialogMessage = (
  message: string,
  highlights?: DialogHighlightKeyword[]
): React.ReactNode => {
  const normalizedHighlights = (highlights || []).filter((item) => String(item.text || '').trim())
  const highlightRegex = buildDialogHighlightRegex(normalizedHighlights)
  const highlightMap = new Map(
    normalizedHighlights.map((item) => [String(item.text || '').trim(), item.tone || 'danger'])
  )
  if (!highlightRegex) {
    return message
  }

  const lines = String(message || '').split('\n')
  return lines.map((line, lineIndex) => {
    const parts = line.split(highlightRegex).filter((part) => part !== '')
    return (
      <React.Fragment key={'line-' + lineIndex}>
        {parts.map((part, partIndex) => {
          const tone = highlightMap.get(part)
          if (!tone) {
            return <React.Fragment key={lineIndex + '-' + partIndex}>{part}</React.Fragment>
          }
          return (
            <span
              key={lineIndex + '-' + partIndex}
              className={
                'mx-0.5 inline-flex rounded-md px-1.5 py-0.5 align-baseline font-black ring-1 '
                + DIALOG_HIGHLIGHT_TONE_CLASSES[tone]
              }
            >
              {part}
            </span>
          )
        })}
        {lineIndex < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    )
  })
}

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useLanguage()
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; variant: DialogVariant }>>([])
  const resolverRef = useRef<((value: boolean) => void) | null>(null)
  const timeoutRef = useRef<Map<string, number>>(new Map())

  const openDialog = useCallback((type: DialogType, message: string, options?: Omit<DialogOptions, 'message'>) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setDialog({
        type,
        message,
        ...options
      })
    })
  }, [])

  const alert = useCallback(
    async (message: string, options?: Omit<DialogOptions, 'message'>) => {
      await openDialog('alert', message, options)
    },
    [openDialog]
  )

  const confirm = useCallback(
    (message: string, options?: Omit<DialogOptions, 'message'>) => openDialog('confirm', message, options),
    [openDialog]
  )

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id))
    const timeoutId = timeoutRef.current.get(id)
    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutRef.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (message: string, options?: Omit<DialogOptions, 'message' | 'title'>) => {
      const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const variant = options?.variant ?? 'info'
      const duration = options?.duration ?? 2600
      setToasts((prev) => [...prev, { id, message, variant }])
      if (duration > 0 && typeof window !== 'undefined') {
        const timeoutId = window.setTimeout(() => {
          removeToast(id)
        }, duration)
        timeoutRef.current.set(id, timeoutId)
      }
    },
    [removeToast]
  )

  const closeDialog = useCallback((result: boolean) => {
    const resolver = resolverRef.current
    resolverRef.current = null
    setDialog(null)
    resolver?.(result)
  }, [])

  useEffect(() => {
    if (!dialog) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDialog(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [dialog, closeDialog])

  const dialogContent = useMemo(() => {
    if (!dialog) {
      return null
    }
    const title = dialog.title || t('common.notice')
    const confirmLabel = dialog.confirmLabel || (dialog.type === 'confirm' ? t('common.confirm') : t('common.ok'))
    const cancelLabel = dialog.cancelLabel || t('common.cancel')
    const variant = dialog.variant ?? 'info'
    const variantStyles: Record<
      DialogVariant,
      { confirm: string; icon: string; iconWrapper: string; panel: string; title: string }
    > = {
      info: {
        confirm: 'bg-primary text-white hover:bg-blue-700 shadow-blue-500/20',
        icon: 'info',
        iconWrapper: 'bg-blue-50 text-blue-600',
        panel: 'border-blue-100',
        title: 'text-slate-900'
      },
      success: {
        confirm: 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-500/20',
        icon: 'check_circle',
        iconWrapper: 'bg-emerald-50 text-emerald-600',
        panel: 'border-emerald-200',
        title: 'text-emerald-700'
      },
      warning: {
        confirm: 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-500/20',
        icon: 'warning',
        iconWrapper: 'bg-amber-50 text-amber-600',
        panel: 'border-amber-200',
        title: 'text-amber-700'
      },
      danger: {
        confirm: 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20',
        icon: 'error',
        iconWrapper: 'bg-red-50 text-red-600',
        panel: 'border-red-200',
        title: 'text-red-600'
      },
      error: {
        confirm: 'bg-red-500 text-white hover:bg-red-600 shadow-red-500/20',
        icon: 'error',
        iconWrapper: 'bg-red-50 text-red-600',
        panel: 'border-red-200',
        title: 'text-red-600'
      }
    }
    const variantStyle = variantStyles[variant]
    return {
      title,
      confirmLabel,
      cancelLabel,
      variant,
      confirmClassName: variantStyle.confirm,
      icon: variantStyle.icon,
      iconWrapperClassName: variantStyle.iconWrapper,
      panelClassName: variantStyle.panel,
      titleClassName: variantStyle.title
    }
  }, [dialog, t])

  const dialogApi = useMemo(
    () => ({ alert, confirm, toast }),
    [alert, confirm, toast]
  )

  return (
    <DialogContext.Provider value={dialogApi}>
      {children}
      {dialog && dialogContent && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center px-4 py-10">
          <button
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => closeDialog(dialog.type === 'confirm' ? false : true)}
          />
          <div
            className={`relative w-full max-w-[720px] max-h-[86vh] overflow-hidden bg-white rounded-2xl border shadow-2xl shadow-slate-200/60 p-6 ${dialogContent.panelClassName}`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 h-9 w-9 rounded-full flex items-center justify-center ${dialogContent.iconWrapperClassName}`}>
                <span className="material-symbols-outlined text-[20px]">{dialogContent.icon}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-lg font-bold ${dialogContent.titleClassName}`}>{dialogContent.title}</div>
                <div className="mt-2 max-h-[58vh] overflow-y-auto break-words pr-2 text-sm text-slate-500 leading-relaxed">
                  {renderDialogMessage(dialog.message, dialog.messageHighlights)}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={() => closeDialog(false)}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  {dialogContent.cancelLabel}
                </button>
              )}
              <button
                onClick={() => closeDialog(true)}
                className={`h-10 px-5 rounded-lg text-sm font-semibold shadow-sm transition-all ${dialogContent.confirmClassName}`}
              >
                {dialogContent.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {toasts.length > 0 && (
        <div className="fixed top-6 right-6 z-[170] flex flex-col gap-3">
          {toasts.map((toastItem) => {
            const tone =
              toastItem.variant === 'danger' || toastItem.variant === 'error'
                ? 'border-red-200 bg-red-50 text-red-600'
                : toastItem.variant === 'warning'
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : toastItem.variant === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-blue-200 bg-blue-50 text-blue-600'
            return (
              <div
                key={toastItem.id}
                className={`min-w-[240px] max-w-[320px] px-4 py-3 rounded-xl border shadow-lg shadow-slate-200/40 text-sm font-semibold flex items-start gap-3 ${tone}`}
              >
                <span className="material-symbols-outlined text-[18px] mt-0.5">
                  {toastItem.variant === 'danger' || toastItem.variant === 'error'
                    ? 'error'
                    : toastItem.variant === 'warning'
                      ? 'warning'
                      : toastItem.variant === 'success'
                        ? 'check_circle'
                      : 'info'}
                </span>
                <div className="flex-1 text-xs leading-relaxed text-slate-700">{toastItem.message}</div>
                <button
                  onClick={() => removeToast(toastItem.id)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
              </div>
            )
          })}
        </div>
      )}
    </DialogContext.Provider>
  )
}

export { useDialog } from './useDialog'
